import { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';
import api, { setCsrfToken, ADMIN_ACCESS_TOKEN_KEY } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch CSRF token on app load
    const initAuth = async () => {
      try {
        const response = await api.get('/auth/csrf-token');
        if (response.data && response.data.csrf_token) {
          setCsrfToken(response.data.csrf_token);
        }
      } catch (error) {
        // Silently fail CSRF fetch (will retry later)
      }

      // Check if admin is already logged in (cookies will be sent automatically)
      try {
        const res = await authApi.getMe();
        setAdmin(res.data);
        localStorage.setItem('gv_admin', JSON.stringify(res.data));
      } catch (error) {
        // Not logged in or token expired - this is normal on login page
        setAdmin(null);
        localStorage.removeItem('gv_admin');
        sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const res = await authApi.login(email, password);
    const { admin: adminData, access_token: accessToken, csrf_token: csrfFromLogin } =
      res.data;
    if (accessToken) {
      sessionStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, accessToken);
    }
    if (csrfFromLogin) {
      setCsrfToken(csrfFromLogin);
    } else {
      try {
        const csrfRes = await authApi.getCsrfToken();
        if (csrfRes.data?.csrf_token) {
          setCsrfToken(csrfRes.data.csrf_token);
        }
      } catch {
        // Request interceptor will read csrf_token from cookie on next PUT/POST
      }
    }
    // Token is also stored in HttpOnly cookie when the browser sends credentials
    setAdmin(adminData);
    localStorage.setItem('gv_admin', JSON.stringify(adminData)); // Cache for display only
    return adminData;
  };

  const logout = async () => {
    try {
      await authApi.logout(); // Clear cookies on backend
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('gv_admin');
      sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
      setAdmin(null);
      // Redirect to login immediately without triggering ProtectedRoute
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ admin, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
